import type { ParentType } from '@risksmart-app/domain/src/types/consts/parent-type';
import { CustomAttributeFieldType } from '@risksmart-app/form-configuration/src/field-types/types';
import type { RelationFile } from '@risksmart-app/shared/forms/shared-schemas/fileSchema';
import type { Parent_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import _ from 'lodash';
import { useCallback, useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';

import type { FilesFromForm } from '@/components/form/controlled-file-list/types';
import { getCustomAttributeRenderProps } from '@/utils/table/utils/customAttributes';
import { useTRPC } from '@/utils/trpc';

import { useRiskSmartForm } from '../form/customisable-form/RiskSmartFormContext';
import { useCustomisableFormDataContext } from '../form/customisable-form-data/CustomisableFormDataContext';

// Bare axios PUT for the presigned S3 upload — no app auth header (the URL is
// pre-signed) and no Sentry interceptor (mirrors `useFileUploadTRPC`).
const s3Client = axios.create();

/**
 * Submit-time pipeline for "Attach file" custom fields (CUS-79). Mounted once
 * per customisable form. Attach-file files behave like the built-in Files
 * field — a field-scoped relation (CUS-78 `relation_custom_attribute_file`),
 * NOT a `CustomAttributeData` value, NOT change-request gated:
 *
 *  - on load (edit): hydrates each field's value from the junction
 *    (`customAttributeFiles.getByParent`) so existing files render.
 *  - after save: presigns + uploads new `File`s, then `customAttributeFiles.sync`
 *    links them to the saved entity id and unlinks removed ones — per field.
 *
 * FormContext strips attach-file fields from the `CustomAttributeData` mutation
 * payload (they live in the junction, not the entity row). trpc-only.
 */
export const useCustomAttributeFileSave = () => {
  const { setAfterSaveHooks, setBeforeSaveHooks, parentType } =
    useRiskSmartForm();
  const { customAttributeSchema } = useCustomisableFormDataContext();
  const { getValues, setValue } = useFormContext();
  const trpc = useTRPC();

  const queryClient = useQueryClient();
  // Destructure mutateAsync (a stable ref) rather than depending on the whole
  // mutation objects, so `afterSave`'s identity stays stable across renders and
  // the registration effect doesn't churn.
  const { mutateAsync: createPresignedUploadUrls } = useMutation(
    trpc.frontend.files.createPresignedUploadUrls.mutationOptions()
  );
  const { mutateAsync: syncCustomAttributeFiles } = useMutation(
    trpc.frontend.customAttributeFiles.sync.mutationOptions()
  );

  const fileFieldPaths = customAttributeSchema
    ? getCustomAttributeRenderProps(
        customAttributeSchema.Schema,
        customAttributeSchema.UiSchema
      )
        .filter((rp) => rp.type === CustomAttributeFieldType.AttachFile)
        .map((rp) => rp.path)
    : [];
  // Stable key so effects only re-run when the set of attach-file fields changes.
  const fileFieldPathsKey = fileFieldPaths.join('|');

  // Entity id (edit only — absent on create). Drives the hydration query.
  const entityId = _.get(getValues(), 'Id') as string | undefined;

  // Existing files for the entity, grouped client-side by FieldId.
  const { data: existingFiles } = useQuery(
    trpc.frontend.customAttributeFiles.getByParent.queryOptions(
      { parentId: entityId ?? '' },
      { enabled: Boolean(entityId) && Boolean(fileFieldPathsKey) }
    )
  );

  // Original linked ids per field — the baseline for the removed-file diff.
  const originalIdsRef = useRef<Record<string, string[]>>({});
  const hydratedRef = useRef(false);
  // Snapshot of each attach-file field's value, captured at submit time by the
  // beforeSave hook (which runs BEFORE the entity mutation). The entity save
  // refetches the parent and react-hook-form re-syncs the form to those values
  // — which never contain attach-file fields (they live in the junction, not
  // CustomAttributeData) — wiping the freshly-selected File before `afterSave`
  // runs. Reading from this snapshot in `afterSave` decouples the upload from
  // that reset (CUS-75).
  const submitSnapshotRef = useRef<Record<string, FilesFromForm>>({});

  // Hydrate the form fields once from the junction so existing files render
  // and the removed diff has a baseline. Re-hydrates if the entity changes.
  useEffect(() => {
    hydratedRef.current = false;
  }, [entityId]);
  useEffect(() => {
    if (!existingFiles || hydratedRef.current || !fileFieldPathsKey) {
      return;
    }
    for (const path of fileFieldPathsKey.split('|')) {
      const files: RelationFile[] = existingFiles
        .filter((f) => f.FieldId === path)
        .map((f) => ({
          Id: f.Id,
          FileName: f.FileName,
          FileSize: f.FileSize,
          ContentType: f.ContentType,
          CreatedAtTimestamp: f.CreatedAtTimestamp,
        }));
      setValue(`CustomAttributeData.${path}`, files);
      originalIdsRef.current[path] = files.map((f) => f.Id);
    }
    hydratedRef.current = true;
  }, [existingFiles, fileFieldPathsKey, setValue]);

  const afterSave = useCallback(
    async (savedId: string | undefined): Promise<void> => {
      if (!savedId || !parentType) {
        return;
      }

      const fields: {
        FieldId: string;
        AddedFiles: {
          FileId: string;
          FileName: string;
          FileSize: number;
          ContentType: string;
        }[];
        RemovedFileIds: string[];
      }[] = [];

      for (const path of fileFieldPathsKey
        ? fileFieldPathsKey.split('|')
        : []) {
        // Read from the submit-time snapshot, NOT getValues() — by the time
        // afterSave runs the entity-save reset has already wiped the live
        // attach-file value (CUS-75).
        const current: FilesFromForm = submitSnapshotRef.current[path] ?? [];
        const files = current.filter(
          (f): f is NonNullable<typeof f> => f != null
        );
        const newFiles = files.filter((f): f is File => f instanceof File);
        const existing = files.filter(
          (f): f is RelationFile => !(f instanceof File)
        );

        const originalIds = originalIdsRef.current[path] ?? [];
        const removedFileIds = originalIds.filter(
          (id) => !existing.some((e) => e.Id === id)
        );

        let addedFiles: {
          FileId: string;
          FileName: string;
          FileSize: number;
          ContentType: string;
        }[] = [];
        if (newFiles.length > 0) {
          const presigned = await createPresignedUploadUrls({
            parentType: parentType as unknown as Parent_Type_Enum,
            fileNames: newFiles.map((f) => f.name),
          });
          await Promise.all(
            presigned.map((p, i) =>
              s3Client.put(p.signedUrl, newFiles[i], {
                headers: { 'Content-Type': newFiles[i].type },
              })
            )
          );
          addedFiles = presigned.map((p, i) => ({
            FileId: p.key,
            FileName: newFiles[i].name,
            FileSize: newFiles[i].size,
            ContentType: newFiles[i].type,
          }));
        }

        if (addedFiles.length > 0 || removedFileIds.length > 0) {
          fields.push({
            FieldId: path,
            AddedFiles: addedFiles,
            RemovedFileIds: removedFileIds,
          });
        }
      }

      if (fields.length > 0) {
        await syncCustomAttributeFiles({
          ParentId: savedId,
          ParentType: parentType as unknown as ParentType,
          Fields: fields,
        });
        // Re-baseline. On full-page edit forms that stay mounted across saves,
        // entityId doesn't change, so the entityId-keyed hydration effect won't
        // re-run on its own. Invalidate the hydration query and clear the
        // hydrated flag so the next save diffs against the freshly-persisted set
        // rather than a stale originalIdsRef (which would re-remove kept files).
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.customAttributeFiles.getByParent.queryKey({
            parentId: savedId,
          }),
        });
        hydratedRef.current = false;
      }
    },
    [
      fileFieldPathsKey,
      parentType,
      createPresignedUploadUrls,
      syncCustomAttributeFiles,
      queryClient,
      trpc,
    ]
  );

  // Capture each attach-file field's value at submit time, BEFORE the entity
  // mutation. beforeSave hooks run ahead of the mutation (and therefore ahead
  // of the post-save form reset that wipes these un-backed fields), so the
  // snapshot still holds the user's freshly-selected File (CUS-75).
  const beforeSave = useCallback(async (): Promise<boolean> => {
    const snapshot: Record<string, FilesFromForm> = {};
    for (const path of fileFieldPathsKey ? fileFieldPathsKey.split('|') : []) {
      snapshot[path] = (_.get(getValues(), `CustomAttributeData.${path}`) ??
        []) as FilesFromForm;
    }
    submitSnapshotRef.current = snapshot;

    return true;
  }, [fileFieldPathsKey, getValues]);

  useEffect(() => {
    if (!fileFieldPathsKey) {
      return;
    }
    setBeforeSaveHooks((prev) => [...prev, beforeSave]);

    return () => {
      setBeforeSaveHooks((prev) => prev.filter((hook) => hook !== beforeSave));
    };
  }, [fileFieldPathsKey, beforeSave, setBeforeSaveHooks]);

  useEffect(() => {
    if (!fileFieldPathsKey) {
      return;
    }
    setAfterSaveHooks((prev) => [...prev, afterSave]);

    return () => {
      setAfterSaveHooks((prev) => prev.filter((hook) => hook !== afterSave));
    };
  }, [fileFieldPathsKey, afterSave, setAfterSaveHooks]);
};
